import { useEffect, useMemo, useState } from 'react';
import { fmtMemberId } from '@lib/member-id';
import { clientDisplayName } from '@lib/clients';
import { getSmsConversation, memberComms } from '@lib/care-api';
import { dialogLines, type ReplyReadingById } from '@lib/sms-dialog';
import {
  CAMPAIGN_META,
  CHANNEL_META,
  STATUS_META,
  fmtPhone,
  fmtTs,
  type EventRow,
  type MemberCommRow,
  type SmsConversationResp,
  type SmsReplyClass,
  type ThreadRow,
} from '@lib/care-model';
import { EventDrawer } from '@components/EventDrawer';
import { SmsDialog } from '@components/SmsDialog';

/**
 * THE MEMBER VIEW UNDER THE ROW (slice B, his img7, 24 Sep 2026): "make so I can open each member and
 * then all that is on the side (the details) is shown below and include the history."
 *
 * Opening a row of the grouped Inbound queue opens this, full width, under the row, instead of the side
 * drawer. Top to bottom:
 *   1. who: the name and the ID (img8), the client, every campaign and cycle the member's entries hold,
 *      the phone, and "Open in side panel" for the old way;
 *   2. HISTORY: every Care entry of the member, oldest first: the queue's own rows (live) merged with
 *      member_care.db's (every entry the builder holds, archived ones included, up to ten minutes
 *      behind), each with its time, channel, status, what it was about and how it ended; a click puts
 *      that entry in the working section;
 *   3. TEXTS: the member's texts out and in, in Frontend G's shared layout (SmsDialog);
 *   4. THE WORKING SECTION: the side drawer's own content for the chosen entry (EventDrawer, variant
 *      'inline'), so every note, status and press is exactly what the drawer does today.
 * Read only apart from section 4, which is the drawer and writes exactly what the drawer writes.
 */

export interface HistoryLine {
  eventId: string;
  receivedTs: string;
  channel: EventRow['channel'];
  status: EventRow['status'];
  subject: string;
  preview: string;
  campaign: string | null;
  cycle: string | null;
  resolution: string;
  /** 'queue' = today's queue list (live); 'db' = member_care.db only (archived or not on this screen). */
  from: 'queue' | 'db';
}

/**
 * The member's history, oldest first: the queue's rows win over member_care.db's for the same entry
 * (they are live; the database is up to ten minutes behind), and the database adds every entry the
 * queue does not show (archived, closed, another tab). Ties broken by the entry id, so the order is fixed.
 */
export function mergeHistory(queue: readonly EventRow[], db: readonly MemberCommRow[]): HistoryLine[] {
  const by = new Map<string, HistoryLine>();
  for (const r of db) {
    by.set(r.eventId, {
      eventId: r.eventId, receivedTs: r.receivedTs, channel: r.channel, status: r.status, subject: r.subject,
      preview: r.preview, campaign: r.campaign, cycle: null, resolution: r.resolution || '', from: 'db',
    });
  }
  for (const r of queue) {
    const had = by.get(r.eventId);
    by.set(r.eventId, {
      eventId: r.eventId, receivedTs: r.receivedTs, channel: r.channel, status: r.status, subject: r.subject,
      preview: r.preview, campaign: r.campaign, cycle: r.cycle ?? null, resolution: had?.resolution ?? '', from: 'queue',
    });
  }
  return [...by.values()].sort((a, b) => a.receivedTs.localeCompare(b.receivedTs) || a.eventId.localeCompare(b.eventId));
}

/** What the member's own entries say about each reply, by the platform's message id (the chip's source). */
export function readingsFrom(rows: readonly EventRow[]): Map<string, ReplyReadingById> {
  const m = new Map<string, ReplyReadingById>();
  for (const r of rows) {
    if (!r.replyMessageId || !r.replyClass) continue;
    m.set(r.replyMessageId, { classification: r.replyClass as SmsReplyClass, intent: r.replyIntent || null, campaign: r.campaign });
  }
  return m;
}

export interface MemberViewProps {
  thread: ThreadRow;
  /** The thread's rows from the queue list, newest first. */
  events: readonly EventRow[];
  selectedEventId: string;
  onSelectEvent: (id: string) => void;
  onClose: () => void;
  onChanged: (updated?: { eventId: string; status: string }) => void;
  onOpenSide: () => void;
}

export function MemberView({ thread, events, selectedEventId, onSelectEvent, onClose, onChanged, onOpenSide }: MemberViewProps) {
  const isMember = thread.kind === 'member' && !!thread.memberId;
  const [db, setDb] = useState<{ state: 'loading' | 'ok' | 'unavailable' | 'error'; rows: MemberCommRow[]; builtAt: string }>({ state: 'loading', rows: [], builtAt: '' });
  const [sms, setSms] = useState<{ state: 'loading' | 'ok' | 'error' | 'none'; data: SmsConversationResp | null }>({ state: 'loading', data: null });
  const campaign = thread.campaigns[0] ?? null;

  useEffect(() => {
    let live = true;
    if (!isMember) {
      setDb({ state: 'ok', rows: [], builtAt: '' });
      return;
    }
    setDb((d) => ({ ...d, state: 'loading' }));
    memberComms([thread.memberId])
      .then((r) => { if (live) setDb({ state: r.available ? 'ok' : 'unavailable', rows: r.rows.filter((x) => x.memberId === thread.memberId), builtAt: r.builtAt }); })
      .catch(() => { if (live) setDb({ state: 'error', rows: [], builtAt: '' }); });
    return () => { live = false; };
  }, [isMember, thread.memberId]);

  useEffect(() => {
    let live = true;
    const args: { memberId?: string; phone?: string; campaign?: string } = {};
    if (isMember) {
      args.memberId = thread.memberId;
      if (campaign) args.campaign = campaign;
    } else if (thread.fromPhone) {
      args.phone = thread.fromPhone;
    } else {
      setSms({ state: 'none', data: null });
      return;
    }
    setSms({ state: 'loading', data: null });
    getSmsConversation(args)
      .then((d) => { if (live) setSms({ state: 'ok', data: d }); })
      .catch(() => { if (live) setSms({ state: 'error', data: null }); });
    return () => { live = false; };
  }, [isMember, thread.memberId, thread.fromPhone, campaign]);

  const history = useMemo(() => mergeHistory(events, db.rows), [events, db.rows]);
  const lines = useMemo(() => dialogLines(sms.data?.messages ?? [], readingsFrom(events)), [sms.data, events]);

  return (
    <div className="member-view" data-testid="member-view">
      {/* 1. WHO */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <b style={{ fontSize: '1.1em' }} data-testid="member-view-name">{isMember ? thread.memberName || 'Name not on file' : thread.label}</b>
        {isMember && <span className="mono muted" data-testid="member-view-id">#{fmtMemberId(thread.memberId)}</span>}
        {thread.client && <span className="pill">{clientDisplayName(thread.client)}</span>}
        {thread.campaigns.map((c) => (
          <span key={c} className={`pill ${CAMPAIGN_META[c]?.pill ?? ''}`.trim()}>{CAMPAIGN_META[c]?.label ?? c}</span>
        ))}
        {(thread.cycles ?? []).map((c) => <span key={c} className="pill" data-testid="member-view-cycle">{c}</span>)}
        {thread.fromPhone && <span className="small">{fmtPhone(thread.fromPhone)}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onOpenSide} title="Open the chosen entry in the side panel instead, as before">
            Open in side panel
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        {/* 2. HISTORY */}
        <section aria-label="History" data-testid="member-history">
          <div className="section-eyebrow" style={{ marginBottom: 4 }}>History ({history.length})</div>
          {db.state === 'unavailable' || db.state === 'error' ? (
            <div className="note small" role="status" data-testid="member-history-partial">
              Older entries could not be read ({db.state === 'error' ? 'the read failed' : 'the database is not available'}); this list shows the queue's entries only.
            </div>
          ) : db.state === 'loading' ? (
            <div className="micro muted">Reading older entries...</div>
          ) : db.builtAt ? (
            <div className="micro muted">Older entries as of {fmtTs(db.builtAt)} (up to ten minutes behind).</div>
          ) : null}
          {history.map((h) => {
            const chan = CHANNEL_META[h.channel] ?? CHANNEL_META.Unknown;
            const st = STATUS_META[h.status] ?? { label: h.status, pill: '' };
            const chosen = h.eventId === selectedEventId;
            return (
              <button
                key={h.eventId}
                type="button"
                className="btn btn-ghost"
                data-testid={`member-history-${h.eventId}`}
                aria-current={chosen ? 'true' : undefined}
                onClick={() => onSelectEvent(h.eventId)}
                style={{
                  display: 'grid', gridTemplateColumns: '128px 70px 1fr auto', gap: 8, width: '100%', textAlign: 'left',
                  alignItems: 'center', padding: '5px 6px', font: 'inherit',
                  background: chosen ? 'var(--paper-3)' : undefined,
                  boxShadow: chosen ? 'inset 3px 0 0 var(--ink-900)' : undefined,
                }}
              >
                <span className="small" style={{ whiteSpace: 'nowrap' }}>{fmtTs(h.receivedTs)}</span>
                <span className={`pill ${chan.pill}`.trim()} style={{ justifySelf: 'start' }}>{chan.label}</span>
                <span className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {h.cycle ? <span className="micro muted">{h.cycle} · </span> : null}
                  {h.subject ? <b>{h.subject} </b> : null}
                  <span className="muted">{h.preview}</span>
                  {h.resolution ? <span className="micro"> · {h.resolution}</span> : null}
                </span>
                <span className={`pill ${st.pill}`.trim()}>{st.label}</span>
              </button>
            );
          })}
        </section>

        {/* 3. TEXTS */}
        <section aria-label="Texts" data-testid="member-texts">
          <div className="section-eyebrow" style={{ marginBottom: 4 }}>Texts</div>
          {sms.state === 'loading' && <div className="micro muted">Reading the texts...</div>}
          {sms.state === 'error' && <div className="note small" role="status">The texts could not be read.</div>}
          {sms.state === 'none' && <div className="small muted">No phone or member to read texts for.</div>}
          {sms.state === 'ok' && <SmsDialog lines={lines} />}
        </section>
      </div>

      {/* 4. THE WORKING SECTION: the side drawer's content, below. */}
      <div style={{ marginTop: 14 }}>
        <EventDrawer key={selectedEventId} eventId={selectedEventId} variant="inline" onClose={onClose} onChanged={onChanged}
          hasAudioHint={events.find((e) => e.eventId === selectedEventId)?.hasAudio ?? false} />
      </div>
    </div>
  );
}
